/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Offcanvas } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaLock, FaMinus, FaPlus, FaShoppingBag, FaTrashAlt } from 'react-icons/fa';
import { cartContext } from '../Context/CartContext';
import { authContext } from '../Context/AuthContext';
import { useCartDrawer } from '../Context/CartDrawerContext';
import styles from './CartDrawer.module.css';

const urlApiPedidos = process.env.REACT_APP_API_PEDIDOS;
const PLACEHOLDER = `${process.env.PUBLIC_URL}/assets/sinimagen.gif`;

const formatear = (valor) => {
    const num = typeof valor === 'number' ? valor : parseFloat(valor);
    if (isNaN(num)) return '0,00';
    return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const urlImagen = (item) => {
    const primera = item.Imagenes ? String(item.Imagenes).split(',')[0].trim() : '';
    return primera ? `${process.env.PUBLIC_URL}/articulos/thumbs/${encodeURIComponent(primera)}` : PLACEHOLDER;
};

const FilaCarrito = ({ item, onSumar, onRestar, onQuitar }) => {
    const [t] = useTranslation('global');
    const [imgFalla, setImgFalla] = useState(false);
    const detalle = `/detalle?sku=${encodeURIComponent(String(item.Alias).replace(/ç/g, ''))}`;
    const subtotal = Number(item.Precio) * item.contador;

    return (
        <li className={styles.fila}>
            <Link to={detalle} className={styles.foto} tabIndex={-1} aria-hidden="true">
                <img
                    src={imgFalla ? PLACEHOLDER : urlImagen(item)}
                    alt=""
                    onError={() => setImgFalla(true)}
                />
            </Link>

            <div className={styles.info}>
                <Link to={detalle} className={styles.titulo}>
                    {item.Nombre || item.Alias}
                </Link>
                <span className={styles.meta}>
                    <span className={styles.sku}>{item.Alias}</span>
                    {item.Marca && <span>{item.Marca}</span>}
                </span>
                <span className={styles.unitario}>
                    {t('carritoLateral.unitario')}: {item.Moneda_Signo} {formatear(item.Precio)}
                </span>

                <div className={styles.filaAbajo}>
                    <div className={styles.stepper} role="group" aria-label={t('store.cantidad')}>
                        <button type="button" onClick={() => onRestar(item)} disabled={item.contador <= 1} aria-label={t('carritoLateral.restar-uno')}>
                            <FaMinus aria-hidden="true" />
                        </button>
                        <span aria-live="polite">{item.contador}</span>
                        <button type="button" onClick={() => onSumar(item)} aria-label={t('carritoLateral.sumar-uno')}>
                            <FaPlus aria-hidden="true" />
                        </button>
                    </div>
                    <strong className={styles.subtotal}>
                        {item.Moneda_Signo} {formatear(subtotal)}
                    </strong>
                </div>
            </div>

            <button
                type="button"
                className={styles.quitar}
                onClick={() => onQuitar(item)}
                aria-label={`${t('carritoLateral.quitar')}: ${item.Nombre || item.Alias}`}
                title={t('carritoLateral.quitar')}
            >
                <FaTrashAlt aria-hidden="true" />
            </button>
        </li>
    );
};

const CartDrawer = () => {
    const [t] = useTranslation('global');
    const navigate = useNavigate();
    const location = useLocation();
    const { abierto, cerrarCarrito } = useCartDrawer();
    const { cartLocal, addToCart, deleteItem, setPrecompra } = useContext(cartContext);
    const { token, user } = useContext(authContext);
    const [procesando, setProcesando] = useState(false);
    const country = process.env.REACT_APP_COUNTRY;

    const items = useMemo(() => (Array.isArray(cartLocal) ? cartLocal : []), [cartLocal]);
    const unidades = items.reduce((total, item) => total + item.contador, 0);

    // Totales por moneda (en ARG conviven pesos y dolares)
    const totales = useMemo(() => {
        const porMoneda = {};
        items.forEach((item) => {
            const signo = country === 'ARG' ? item.Moneda_Signo || '' : 'U$S';
            porMoneda[signo] = (porMoneda[signo] || 0) + Number(item.Precio) * item.contador;
        });
        return Object.entries(porMoneda);
    }, [items, country]);

    const impuesto = country === 'PA' ? 'ITBMS' : 'IVA';

    // Se cierra al cambiar de pagina o si se pierde la sesion
    useEffect(() => {
        cerrarCarrito();
    }, [location.pathname, location.search, cerrarCarrito]);

    useEffect(() => {
        if (!token) cerrarCarrito();
    }, [token, cerrarCarrito]);

    const sumar = (item) => {
        if (item.contador < item.Stock) addToCart(item, 1);
        else toast.error(t('producto.stock-insuficiente'), { autoClose: 400 });
    };
    const restar = (item) => {
        if (item.contador > 1) addToCart(item, -1);
    };
    const quitar = (item) => deleteItem(item.Alias);

    // Misma logica que el boton "Confirmar compra" de /cart: genera la precompra (proforma)
    // y recien despues lleva a /checkout, que lee esa precompra.
    const finalizarCompra = async () => {
        if (procesando || items.length === 0) return;
        setProcesando(true);
        try {
            const tipoEntrega = {
                Codigo: user.Tipo_entrega.Codigo,
                Descripcion: user.Tipo_entrega.Descripcion,
            };
            const precompra = {
                items: items.map((item) => ({
                    Alias: item.Alias,
                    Precio: item.Precio,
                    Cantidad: item.contador,
                    Moneda: item.Moneda_Signo,
                })),
                cond_pago: {
                    Codigo: user.Condicion_Pago.Codigo,
                    Descripcion: user.Condicion_Pago.Descripcion,
                },
                tipo_entrega: tipoEntrega,
                Direccion_entrega:
                    tipoEntrega.Descripcion === 'ENTREGA'
                        ? {
                            Domicilio: user.Domicilio_entrega.Domicilio,
                            Localidad: user.Domicilio_entrega.Localidad,
                            codigoPostal: user.Domicilio_entrega.Codigo_postal,
                            Codigo_Prov: user.Domicilio_entrega.Codigo_Prov,
                            Pais: user.Domicilio_entrega.Pais,
                        }
                        : null,
            };

            const res = await axios.post(
                `${urlApiPedidos}/proforma`,
                { precompra },
                { headers: { Authorization: 'Bearer ' + token } }
            );
            setPrecompra(res.data);
            cerrarCarrito();
            navigate('/checkout');
        } catch (err) {
            console.log('ERROR CART', err);
            toast.error(t('carritoLateral.error'));
        } finally {
            setProcesando(false);
        }
    };

    const irAExplorar = () => {
        cerrarCarrito();
        navigate(country === 'JM' || country === 'TT' || country === 'PR' ? '/' : '/store');
    };

    if (!token) return null;

    return (
        <Offcanvas
            show={abierto}
            onHide={cerrarCarrito}
            placement="end"
            className={styles.drawer}
            backdropClassName={styles.backdrop}
            aria-labelledby="cartDrawerTitulo"
        >
            <Offcanvas.Header closeButton closeVariant="white" closeLabel={t('carritoLateral.cerrar')} className={styles.cabecera}>
                <Offcanvas.Title id="cartDrawerTitulo" className={styles.cabeceraTitulo}>
                    <FaShoppingBag aria-hidden="true" />
                    <span>{t('carritoLateral.titulo')}</span>
                    {unidades > 0 && (
                        <small>{unidades === 1 ? t('carritoLateral.unidad-una') : t('carritoLateral.unidades-n', { n: unidades })}</small>
                    )}
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className={styles.cuerpo}>
                {items.length === 0 ? (
                    <div className={styles.vacio}>
                        <div className={styles.vacioIcono} aria-hidden="true">
                            <FaShoppingBag />
                        </div>
                        <h3>{t('carrito.carrito-vacio')}</h3>
                        <p>{t('carritoLateral.vacio-texto')}</p>
                        <button type="button" className={`${styles.btn} ${styles.btnPrimario}`} onClick={irAExplorar}>
                            {t('carritoLateral.explorar')}
                        </button>
                    </div>
                ) : (
                    <>
                        <ul className={styles.lista}>
                            {items.map((item) => (
                                <FilaCarrito key={item.Alias} item={item} onSumar={sumar} onRestar={restar} onQuitar={quitar} />
                            ))}
                        </ul>

                        <div className={styles.pie}>
                            <div className={styles.totales}>
                                <span className={styles.totalLabel}>
                                    {country === 'USA'
                                        ? t('pedidos.total')
                                        : t('carritoLateral.total-sin', { tax: impuesto })}
                                </span>
                                <div className={styles.totalValores}>
                                    {totales.map(([signo, valor]) => (
                                        <strong key={signo}>
                                            {signo} {formatear(valor)}
                                        </strong>
                                    ))}
                                </div>
                            </div>

                            {country !== 'USA' && (
                                <p className={styles.nota}>
                                    * {t('carrito.calculo-1')}
                                    {impuesto}
                                    {t('carrito.calculo-2')}
                                </p>
                            )}

                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnPrimario} ${styles.btnFinalizar}`}
                                onClick={finalizarCompra}
                                disabled={procesando}
                            >
                                <FaLock aria-hidden="true" />
                                {procesando ? t('carritoLateral.procesando') : t('carritoLateral.finalizar')}
                            </button>
                            <button type="button" className={`${styles.btn} ${styles.btnVidrio}`} onClick={cerrarCarrito}>
                                {t('carritoLateral.seguir')}
                            </button>
                        </div>
                    </>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default CartDrawer;
